import {
  AddressDto,
  AuditMemberTransferRequestFormDto,
  AuditMemberTransferRequestFormSchema,
  MemberTransferRequestDto,
  UserDto,
} from '@act/shared/data-transfer-objects';
import {
  Button,
  FormBuilder,
  Information,
  InformationView,
} from '@act/storybook';
import { zodResolver } from '@hookform/resolvers/zod';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import {
  Card,
  CardActions,
  CardContent,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Typography,
} from '@mui/material';
import Dialog from '@mui/material/Dialog';
import { DateTime } from 'luxon';
import { useEffect } from 'react';
import { useToast } from 'react-components/app/ReusableToast';
import { TrackingProvider, useTracking } from 'react-components/tracking';
import { useForm } from 'react-hook-form';
import { memberTransferRequests } from 'redux/requests/member-transfer.request';
import {
  auditMemberTransferRequestSuccess,
  getMemberTransferReasons,
  memberTransferRequestSelectors,
} from 'redux/slices/memberTransferRequestSlice';
import { selectViewable } from 'redux/slices/usersSlice';
import { useAppDispatch, useAppSelector } from 'redux/store-hooks';
import { useApiCall } from 'redux/utils/api-hook';
import { FormConfig } from 'stories/forms/FormBuilder/FormBuilder.types';
import styled from 'styled-components';
import { reemaColors } from 'toolkit/styles/colors';
import Loader from '../workflows/loader';
import useUnsavedChangesDialog from './hooks/use-unsaved-changes-dialog';

const CardTitleDiv = styled.div`
  margin-bottom: 8px;
`;

const CardInformationDiv = styled.div`
  max-width: 525px;
`;

interface TransferRequestDialogProps {
  requestMember: UserDto;
  transferRequest?: MemberTransferRequestDto;
  open: boolean;
  handleClose: () => void;
}

const TransferRequestDialog = ({
  requestMember,
  transferRequest,
  open,
  handleClose,
}: TransferRequestDialogProps) => {
  const dispatch = useAppDispatch();
  const toast = useToast();
  const handleUnsavedChanges = useUnsavedChangesDialog();
  const memberTransferReasons =
    memberTransferRequestSelectors.useSelectMemberTransferReasons();
  const memberTransferLoadingStatus =
    memberTransferRequestSelectors.useSelectMemberTransferSliceStatuses();

  const { control, setValue, formState, handleSubmit } =
    useForm<AuditMemberTransferRequestFormDto>({
      defaultValues: {
        approval: null,
        receivingGuideId: transferRequest?.receivingGuideId,
        effectiveAt: transferRequest?.timestampDetail
          ? DateTime.fromMillis(transferRequest?.timestampDetail).toFormat(
              'yyyy-MM-dd'
            )
          : DateTime.now().toFormat('yyyy-MM-dd'),
        auditorComment: '',
      },
      resolver: zodResolver(AuditMemberTransferRequestFormSchema),
    });

  const viewableGuides = useAppSelector(selectViewable);
  const { isValid, isDirty } = formState;

  useEffect(() => {
    if (transferRequest) {
      setValue('receivingGuideId', transferRequest?.receivingGuideId);
    }
  }, [transferRequest]);

  useEffect(() => {
    async function dispatchGetMemberTransferReasons() {
      await dispatch(await getMemberTransferReasons({ params: null }));
    }

    if (
      !memberTransferReasons.length &&
      memberTransferLoadingStatus.reasonsStatus === 'initial'
    ) {
      void dispatchGetMemberTransferReasons();
    }
  }, [
    dispatch,
    memberTransferLoadingStatus.reasonsStatus,
    memberTransferReasons.length,
  ]);
  const [
    auditMemberTransferResult,
    auditMemberTransferLoadingStatus,
    auditMemberTransfer,
  ] = useApiCall(memberTransferRequests.auditMemberTransferRequest);

  function gatherInformation(): Information[] {
    const transferReason = memberTransferReasons?.find(
      reason => reason.id === transferRequest?.transferReasonId
    );

    function constructAddress(address: AddressDto): string {
      return `${address?.line1}, ${
        address?.line2 ? address?.line2 + ', ' : ''
      }${address?.city}, ${address?.state} ${address?.zip}`;
    }

    const detailValue =
      transferReason?.detailType === 'address'
        ? constructAddress(
            requestMember?.addresses?.find(
              a => a.id === +transferRequest?.numericDetail
            )
          )
        : transferReason?.detailType === 'date'
        ? DateTime.fromMillis(transferRequest?.timestampDetail).toFormat(
            'MM-dd-yyyy'
          )
        : transferRequest?.stringDetail;

    return [
      {
        label: 'Member',
        value: requestMember?.name,
      },
      {
        label: 'Reason',
        value: transferReason?.text,
      },
      {
        label: transferReason?.detailLabel,
        value: detailValue,
      },
      {
        label: 'New guide',
        value: viewableGuides?.find(
          guide => guide.id === transferRequest?.receivingGuideId
        )?.name,
      },
      {
        label: 'Comments',
        value: transferRequest?.commentToReceivingGuide,
      },
    ];
  }

  useEffect(() => {
    if (auditMemberTransferResult && !auditMemberTransferLoadingStatus) {
      dispatch(auditMemberTransferRequestSuccess(auditMemberTransferResult));
      toast('Transfer request audited successfully');
      handleClose();
    }
  }, [auditMemberTransferResult, auditMemberTransferLoadingStatus, dispatch]);

  const onSubmit = (data: AuditMemberTransferRequestFormDto) => {
    void auditMemberTransfer({
      params: { id: transferRequest?.id },
      body: {
        approval: data.approval,
        receivingGuideId: data.receivingGuideId,
        effectiveAt: DateTime.fromFormat(
          data.effectiveAt,
          'yyyy-MM-dd'
        ).toMillis(),
        auditorComment: data.auditorComment,
      },
    });
  };

  const formConfig: FormConfig<AuditMemberTransferRequestFormDto> = [
    {
      type: 'input',
      name: 'receivingGuideId',
      label: 'Select a Guide',
      input: 'select',
      props: {
        options: viewableGuides?.map(guide => ({
          label: guide.name,
          value: guide.id,
        })),
        trackingName: 'receiving-guide-select',
      },
    },
    {
      type: 'label',
      label: 'Approve or deny the request.',
    },
    {
      type: 'input',
      name: 'approval',
      label: 'Approval or denial',
      input: 'radioGroup',
      props: {
        options: [
          { label: 'Approve', value: 'approve' },
          { label: 'Deny', value: 'deny' },
        ],
        trackingName: 'approval-radio-group',
      },
    },
    {
      type: 'label',
      label: 'When does this transfer take effect?',
    },
    {
      type: 'input',
      name: 'effectiveAt',
      label: 'Effective date',
      input: 'date',
      props: {
        inputType: 'date',
        trackingName: 'effective-at-date-input',
      },
    },

    {
      type: 'label',
      label: 'Explain the reason(s) for the decision.',
    },
    {
      type: 'input',
      name: 'auditorComment',
      label: 'Comments',
      input: 'text',
      props: {
        multiline: true,
        characterLimit: 500,
        trackingName: 'auditor-comment-input',
      },
    },
  ];

  const closeDialog = () => {
    if (isDirty) {
      void handleUnsavedChanges(() => {
        handleClose();
      });
    } else {
      handleClose();
    }
  };

  const { generateTrackingProps } = useTracking();
  return (
    <TrackingProvider contextName="audit-transfer-request-dialog">
      <Dialog
        open={open}
        onClose={closeDialog}
        fullScreen
        hideBackdrop
        sx={{
          marginTop: '51px',
          '& MuiModal-backdrop': {
            marginTop: '51px',
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: '44px',
            padding: '18px 36px',
            boxShadow:
              '0px 1px 2px rgba(0, 0, 0, 0.16), 0px 1px 3px 1px rgba(0, 0, 0, 0.08)',
            zIndex: '2',
          }}
        >
          <IconButton
            onClick={handleClose}
            {...generateTrackingProps('close-button')}
          >
            <ArrowBackIcon />
          </IconButton>
          Member transfer review
        </DialogTitle>
        <DialogContent
          sx={{
            display: 'grid',
            justifyContent: 'center',
            paddingTop: '12px !important',
            backgroundColor: reemaColors.lightBackground,
            zIndex: '1',
          }}
        >
          {memberTransferLoadingStatus.reasonsStatus !== 'success' && (
            <Loader />
          )}
          {memberTransferLoadingStatus.reasonsStatus === 'success' && (
            <Card
              sx={{
                height: 'min-content',
                maxWidth: '570px',
              }}
            >
              <CardContent
                sx={{
                  padding: '24px 24px 0',
                  '&:last-child': {
                    paddingBottom: 0,
                  },
                }}
              >
                <CardTitleDiv>
                  <Typography variant="titleLarge">
                    Approve or deny the transfer request.
                  </Typography>
                </CardTitleDiv>
                <CardInformationDiv>
                  <Typography variant="bodyLarge">
                    Evaluate whether there is necessity or cause for a change in
                    status or guide assignment for the member.
                  </Typography>
                </CardInformationDiv>
                <InformationView information={gatherInformation()} />
                {!viewableGuides?.length && <Loader />}
                {viewableGuides?.length && (
                  <FormBuilder
                    config={formConfig}
                    control={control}
                    formState={formState}
                  />
                )}
              </CardContent>
              <Divider sx={{ marginTop: '48px' }} />
              <CardActions
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'right',
                  padding: '8px 24px',
                }}
              >
                <Button onClick={closeDialog}>Cancel</Button>
                <Button
                  disabled={!isValid}
                  onClick={handleSubmit(onSubmit)}
                  variant="contained"
                  debounced
                >
                  Send review
                </Button>
              </CardActions>
            </Card>
          )}
        </DialogContent>
      </Dialog>
    </TrackingProvider>
  );
};

export default TransferRequestDialog;
